//* 장례식장 하나에 대해 부고브랜드별 꽃집(DN)을 연결하는 패널
//* 장례식장 생성 직후 / 장례식장 수정 페이지에서 공용으로 사용
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { useSimpleBugoBrandListHook } from '@shared/api/bugoBrand/bugoBrand.hook';
import { BugoBrand } from '@shared/api/bugoBrand/bugoBrand.interface';
import { simpleBugoBrandListStateSelector } from '@shared/api/bugoBrand/bugoBrand.selector';
import { DistributionNetwork } from '@shared/api/distributionNetwork/distributionNetwork.interface';
import { useSellerShopListHook } from '@shared/api/shop/shop.hook';
import { Shop } from '@shared/api/shop/shop.interface';
import { sellerShopListStateSelector } from '@shared/api/shop/shop.selector';
import { Button } from '@shared/components/Button';
import { queryString } from '@shared/hooks/recoil-query';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { api_ } from '@shared/plugIn/axios';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { Select, Spin, Tag } from 'antd';

type DnEntry = DistributionNetwork<string, string, Shop | string>;

type SaveOperation =
  | { type: 'create'; bugoBrandId: string; shopId: string }
  | { type: 'patch'; dnId: string; shopId: string };

//* 요청 폭주 방지용 동시 실행 개수
const REQUEST_CHUNK_SIZE = 5;

const getBrandName = (brand: BugoBrand) => brand.bugoAgency?.corpName ?? brand._id;

const getShopId = (shop: Shop | string | undefined) => {
  if (!shop) return undefined;
  return typeof shop === 'string' ? shop : shop._id;
};

interface Props {
  funeralHomeInfoId: string;
  //* 저장 완료 후 콜백 (모달 닫기 등)
  onSaved?: () => void;
  //* 나중에 설정 버튼 (없으면 버튼 미노출)
  onSkip?: () => void;
  skipLabel?: string;
}

export const AdminFuneralHomeDnAssignPanel: FC<Props> =
  function AdminFuneralHomeDnAssignPanel({
    funeralHomeInfoId,
    onSaved,
    onSkip,
    skipLabel = '나중에 설정',
  }: Props) {
    useSimpleBugoBrandListHook();
    useSellerShopListHook();

    const simpleBugoBrandState = useRecoilValue(simpleBugoBrandListStateSelector);
    const shopListState = useRecoilValue(sellerShopListStateSelector);
    const notiInstance = useRecoilValue(notificationInstanceAtom);

    //* 선택 상태를 서버값으로 시딩한 장례식장 id (재조회 덮어쓰기 방지)
    const seededIdRef = useRef<string | null>(null);
    const [dnList, setDnList] = useState<DnEntry[]>([]);
    const [dnLoading, setDnLoading] = useState(true);
    //* bugoBrandId -> shopId
    const [selection, setSelection] = useState<Record<string, string | undefined>>({});
    const [bulkShopId, setBulkShopId] = useState<string | undefined>(undefined);
    const [saving, setSaving] = useState(false);
    //* 브랜드별 목록 펼침 (전부 지정된 상태면 접어두고 요약만 보여줌)
    const [listOpen, setListOpen] = useState(true);
    //* 펼침 기본값을 정한 장례식장 id (사용자가 접었는데 다시 열리는 것 방지)
    const autoOpenDecidedRef = useRef<string | null>(null);

    const bugoBrandList = useMemo(() => {
      if (simpleBugoBrandState.status !== 'success' || !simpleBugoBrandState.data)
        return [];
      return [...simpleBugoBrandState.data].sort((a, b) =>
        getBrandName(a).localeCompare(getBrandName(b)),
      );
    }, [simpleBugoBrandState.status, simpleBugoBrandState.data]);

    const shopNameMap = useMemo(() => {
      const map = new Map<string, string>();
      if (shopListState.status !== 'success' || !shopListState.data) return map;
      shopListState.data.forEach((shop) => map.set(shop._id, shop.name));
      return map;
    }, [shopListState.status, shopListState.data]);

    const shopOptions = useMemo(() => {
      if (shopListState.status !== 'success' || !shopListState.data) return [];
      return [...shopListState.data]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((shop) => ({ label: shop.name, value: shop._id }));
    }, [shopListState.status, shopListState.data]);

    //* bugoBrandId -> 이미 존재하는 DN (중복 시 첫번째 기준)
    const { dnMap, duplicatedBrandIds } = useMemo(() => {
      const map = new Map<string, DnEntry>();
      const duplicated = new Set<string>();
      dnList.forEach((dn) => {
        if (!dn.bugoBrand) return;
        if (map.has(dn.bugoBrand)) {
          duplicated.add(dn.bugoBrand);
          return;
        }
        map.set(dn.bugoBrand, dn);
      });
      return { dnMap: map, duplicatedBrandIds: duplicated };
    }, [dnList]);

    const fetchDnList = useCallback(
      //* reseed: 저장 직후처럼 서버값으로 선택 상태를 다시 맞춰야 할 때만 true
      async (reseed = false) => {
        setDnLoading(true);
        try {
          const { data } = await api_.get<DnEntry[]>(
            `admin/distribution-network/?${queryString({
              filter: { funeralHomeInfo: funeralHomeInfoId },
              populate: [{ path: 'shop' }],
            })}`,
          );
          setDnList(data);
          //* 재조회가 편집중인 선택값을 덮어쓰지 않도록 최초 1회(또는 명시적 reseed)만 시딩
          if (reseed || seededIdRef.current !== funeralHomeInfoId) {
            const seeded: Record<string, string | undefined> = {};
            data.forEach((dn) => {
              if (!dn.bugoBrand || seeded[dn.bugoBrand]) return;
              seeded[dn.bugoBrand] = getShopId(dn.shop);
            });
            setSelection(seeded);
            seededIdRef.current = funeralHomeInfoId;
          }
        } catch (err) {
          console.error(err);
          notificateError(notiInstance, 'DN 연결현황 조회 실패');
        } finally {
          setDnLoading(false);
        }
      },
      [funeralHomeInfoId, notiInstance],
    );

    useEffect(() => {
      fetchDnList();
    }, [fetchDnList]);

    const assignedCount = useMemo(
      () => bugoBrandList.filter((brand) => selection[brand._id]).length,
      [bugoBrandList, selection],
    );
    const missingCount = bugoBrandList.length - assignedCount;

    //* 꽃집별 지정 개수 요약 (예: 마리 16 / 가온 1)
    const shopSummary = useMemo(() => {
      const counts = new Map<string, number>();
      bugoBrandList.forEach((brand) => {
        const shopId = selection[brand._id];
        if (!shopId) return;
        counts.set(shopId, (counts.get(shopId) ?? 0) + 1);
      });
      return Array.from(counts.entries())
        .map(([shopId, count]) => ({
          shopId,
          count,
          name: shopNameMap.get(shopId) ?? '이름 미상',
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    }, [bugoBrandList, selection, shopNameMap]);

    const saveOperations: SaveOperation[] = useMemo(() => {
      const operations: SaveOperation[] = [];
      bugoBrandList.forEach((brand) => {
        const shopId = selection[brand._id];
        if (!shopId) return;
        const existingDn = dnMap.get(brand._id);
        if (!existingDn) {
          operations.push({ type: 'create', bugoBrandId: brand._id, shopId });
          return;
        }
        if (getShopId(existingDn.shop) !== shopId) {
          operations.push({ type: 'patch', dnId: existingDn._id, shopId });
        }
      });
      return operations;
    }, [bugoBrandList, selection, dnMap]);

    const applyBulk = useCallback(
      (onlyMissing: boolean) => {
        if (!bulkShopId) return;
        setSelection((prev) => {
          const next = { ...prev };
          bugoBrandList.forEach((brand) => {
            if (onlyMissing && next[brand._id]) return;
            next[brand._id] = bulkShopId;
          });
          return next;
        });
      },
      [bulkShopId, bugoBrandList],
    );

    const handleSave = useCallback(async () => {
      if (saveOperations.length === 0) return;

      setSaving(true);
      let failedCount = 0;
      try {
        for (let i = 0; i < saveOperations.length; i += REQUEST_CHUNK_SIZE) {
          const chunk = saveOperations.slice(i, i + REQUEST_CHUNK_SIZE);
          const results = await Promise.allSettled(
            chunk.map((operation) => {
              if (operation.type === 'create') {
                return api_.post('admin/distribution-network', {
                  bugoBrand: operation.bugoBrandId,
                  funeralHomeInfo: funeralHomeInfoId,
                  shop: operation.shopId,
                });
              }
              return api_.patch(`admin/distribution-network/${operation.dnId}`, {
                shop: operation.shopId,
              });
            }),
          );
          results.forEach((result) => {
            if (result.status === 'rejected') {
              failedCount += 1;
              console.error(result.reason);
            }
          });
        }

        if (failedCount > 0) {
          notificateError(
            notiInstance,
            `${saveOperations.length - failedCount}개 저장 / ${failedCount}개 실패`,
          );
          await fetchDnList(true);
          return;
        }

        notificateSuccess(notiInstance, `꽃집 연결 ${saveOperations.length}개 저장 완료`);
        await fetchDnList(true);
        onSaved?.();
      } catch (err) {
        console.error(err);
        notificateError(notiInstance, '꽃집 연결 저장 실패');
      } finally {
        setSaving(false);
      }
    }, [saveOperations, funeralHomeInfoId, notiInstance, fetchDnList, onSaved]);

    const handleSkip = useCallback(() => {
      if (!onSkip) return;
      const confirmed = window.confirm(
        '꽃집을 연결하지 않으면 이 장례식장의 부고에서 화환 주문 목록이 뜨지 않습니다.\n나중에 DN 관리에서 직접 설정해야 합니다. 그래도 넘어갈까요?',
      );
      if (!confirmed) return;
      onSkip();
    }, [onSkip]);

    const loading =
      dnLoading ||
      simpleBugoBrandState.status === 'loading' ||
      shopListState.status === 'loading';

    //* 최초 로드 시 미지정이 있으면 펼치고, 전부 지정돼 있으면 접어둔다
    useEffect(() => {
      if (loading || bugoBrandList.length === 0) return;
      if (autoOpenDecidedRef.current === funeralHomeInfoId) return;
      autoOpenDecidedRef.current = funeralHomeInfoId;
      setListOpen(missingCount > 0);
    }, [loading, bugoBrandList.length, missingCount, funeralHomeInfoId]);

    return (
      <div className="space-y-3">
        <p className="text-13 text-gray-500">
          부고브랜드별로 꽃집을 연결해야 해당 브랜드 부고에서 화환 주문 목록이 뜹니다.
        </p>

        <div className="flex flex-wrap items-center gap-2 rounded-md bg-gray-50 p-3">
          <span className="text-13 font-medium">전체 일괄 지정</span>
          <Select
            placeholder="꽃집 선택"
            options={shopOptions}
            value={bulkShopId}
            onChange={(shopId: string) => setBulkShopId(shopId)}
            style={{ minWidth: 180 }}
            size="small"
            showSearch
            filterOption={(input, option) =>
              (option?.label as string)?.toLowerCase().includes(input.toLowerCase()) ??
              false
            }
          />
          <Button
            disabled={!bulkShopId}
            className="rounded bg-blue-500 px-3 py-1 text-13 text-white disabled:bg-gray-300"
            onClick={() => applyBulk(false)}
          >
            전체 적용
          </Button>
          {assignedCount > 0 && missingCount > 0 && (
            <Button
              disabled={!bulkShopId}
              className="rounded border border-blue-400 px-3 py-1 text-13 text-blue-600 disabled:border-gray-300 disabled:text-gray-400"
              onClick={() => applyBulk(true)}
            >
              미지정만 적용
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-13">
          <span>
            부고브랜드 <strong>{bugoBrandList.length}</strong>개
          </span>
          {shopSummary.map((summary) => (
            <Tag key={summary.shopId} color="blue" className="m-0">
              {`${summary.name} ${summary.count}`}
            </Tag>
          ))}
          <Tag color={missingCount === 0 ? 'green' : 'red'} className="m-0">
            {missingCount === 0 ? '미지정 없음' : `미지정 ${missingCount}`}
          </Tag>
          {duplicatedBrandIds.size > 0 && (
            <Tag color="orange" className="m-0">
              {`DN 중복 ${duplicatedBrandIds.size}`}
            </Tag>
          )}
          <Button
            className="rounded border border-gray-300 px-2 py-0.5 text-13"
            onClick={() => setListOpen((prev) => !prev)}
          >
            {listOpen ? '접기' : '브랜드별 보기'}
          </Button>
        </div>

        {listOpen && (
          <Spin spinning={loading}>
            <div className="max-h-[45vh] space-y-1 overflow-y-auto rounded-md border border-gray-200 p-2">
              {bugoBrandList.map((brand) => {
                const existingDn = dnMap.get(brand._id);
                const existingShopId = getShopId(existingDn?.shop);
                const selectedShopId = selection[brand._id];
                return (
                  <div
                    key={brand._id}
                    className="flex items-center justify-between gap-2 border-b border-gray-100 py-1 last:border-b-0"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-13 font-medium">
                        {getBrandName(brand)}
                      </p>
                      {duplicatedBrandIds.has(brand._id) && (
                        <p className="text-11 text-orange-600">
                          DN 중복 - DN 관리에서 정리 필요
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {selectedShopId && existingShopId !== selectedShopId && (
                        <Tag color={existingDn ? 'orange' : 'green'}>
                          {existingDn ? '변경' : '신규'}
                        </Tag>
                      )}
                      <Select
                        placeholder="꽃집 선택"
                        options={shopOptions}
                        value={selectedShopId}
                        onChange={(shopId: string) =>
                          setSelection((prev) => ({ ...prev, [brand._id]: shopId }))
                        }
                        style={{ width: 180 }}
                        size="small"
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label as string)
                            ?.toLowerCase()
                            .includes(input.toLowerCase()) ?? false
                        }
                      />
                    </div>
                  </div>
                );
              })}
              {!loading && bugoBrandList.length === 0 && (
                <p className="py-4 text-center text-13 text-gray-400">
                  부고브랜드가 없습니다.
                </p>
              )}
            </div>
          </Spin>
        )}

        <div className="flex items-center gap-2">
          <Button
            disabled={saving || loading || saveOperations.length === 0}
            className="button-rectangle h-10 flex-1 rounded-md theme-text-3 theme-bg-main disabled:bg-gray-300"
            onClick={handleSave}
          >
            {saveOperations.length > 0
              ? `저장 (${saveOperations.length}건)`
              : '저장할 변경사항 없음'}
          </Button>
          {onSkip && (
            <Button
              disabled={saving}
              className="button-rectangle h-10 rounded-md border border-gray-300 px-4"
              onClick={handleSkip}
            >
              {skipLabel}
            </Button>
          )}
        </div>
      </div>
    );
  };
